import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import { StatusBadge } from '@/shared/components/ui/StatusBadge';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import TablePagination from '@mui/material/TablePagination';
import Alert from '@mui/material/Alert';
import Grid from '@mui/material/Grid2';
import PeopleIcon from '@mui/icons-material/People';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Stack from '@mui/material/Stack';
import { adminApi, adminKeys } from '@/features/admin/api/admin.api';
import { AdminPageHeader } from '@/features/admin/components/AdminPageHeader';
import { StatCard } from '@/features/admin/components/StatCard';
import { DeleteAccountDialog } from '@/features/admin/components/DeleteAccountDialog';
import { UserRole } from '@/types';
import type { User } from '@/types';

export function AdminTravelersPage() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [pendingDelete, setPendingDelete] = useState<User | null>(null);

  const { data: stats } = useQuery({
    queryKey: adminKeys.dashboard(),
    queryFn: adminApi.getDashboard,
  });

  const { data, isLoading, isError } = useQuery({
    queryKey: adminKeys.users({ page: page + 1, limit: rowsPerPage, role: UserRole.TRAVELER }),
    queryFn: () =>
      adminApi.listUsers({ page: page + 1, limit: rowsPerPage, role: UserRole.TRAVELER }),
  });

  const deactivateMutation = useMutation({
    mutationFn: (userId: string) => adminApi.deactivateUser(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.users() });
      queryClient.invalidateQueries({ queryKey: adminKeys.dashboard() });
    },
  });

  // Server-side switch (ADMIN_DELETE_ENABLED). Absent/false hides the button.
  const canDelete = stats?.capabilities?.canDeleteAccounts === true;

  const deleteMutation = useMutation({
    mutationFn: (userId: string) => adminApi.deleteUser(userId),
    onSuccess: () => {
      setPendingDelete(null);
      // Removing the last row of a trailing page would leave an empty table.
      if (data?.data?.length === 1 && page > 0) setPage((current) => current - 1);
      // A deletion moves the counters as well as the list, so refetch the lot.
      queryClient.invalidateQueries({ queryKey: adminKeys.all });
    },
  });

  const deleteErrorMessage = deleteMutation.isError
    ? ((deleteMutation.error as { response?: { data?: { message?: string } } })?.response?.data
        ?.message ?? 'Failed to delete this traveler. Please try again.')
    : null;

  const openDeleteDialog = (user: User) => {
    deleteMutation.reset();
    setPendingDelete(user);
  };

  const travelerName = (user: User) =>
    [user.firstName, user.lastName].filter(Boolean).join(' ') ||
    user.phone ||
    user.email ||
    'this traveler';

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  return (
    <Box>
      <AdminPageHeader
        title="Travelers"
        subtitle="All registered travelers on the platform"
      />

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <StatCard
            label="Total Travelers"
            value={stats?.totalTravelers ?? '—'}
            icon={<PeopleIcon />}
            accent="primary.main"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <StatCard
            label="New This Week"
            value={stats?.recentTravelerSignups ?? '—'}
            subtitle="Last 7 days"
            icon={<PeopleIcon />}
            accent="success.main"
          />
        </Grid>
      </Grid>

      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2.5, overflow: 'hidden' }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : isError ? (
          <Alert severity="error" sx={{ m: 2 }}>Failed to load travelers</Alert>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>User</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Contact</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Auth</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Connections</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Joined</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Last Login</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="right">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!data?.data?.length ? (
                    <TableRow>
                      <TableCell colSpan={8} sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
                        No travelers found
                      </TableCell>
                    </TableRow>
                  ) : (
                    data.data.map((user) => (
                      <TableRow key={user.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {[user.firstName, user.lastName].filter(Boolean).join(' ') || '—'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {user.country || 'No country'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{user.phone || '—'}</Typography>
                          <Typography variant="caption" color="text.secondary">{user.email || ''}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={user.authProvider ?? '—'} size="small" variant="outlined" sx={{ textTransform: 'capitalize', borderColor: 'divider' }} />
                        </TableCell>
                        <TableCell>{user.freeConnectionsRemaining ?? 0}</TableCell>
                        <TableCell>{formatDate(user.createdAt)}</TableCell>
                        <TableCell>{formatDate(user.lastLoginAt)}</TableCell>
                        <TableCell>
                          <StatusBadge
                            status={user.isActive === false ? 'rejected' : 'approved'}
                            label={user.isActive === false ? 'Deactivated' : 'Active'}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            {user.isActive !== false && (
                              <Button
                                size="small"
                                color="error"
                                variant="outlined"
                                disabled={deactivateMutation.isPending}
                                onClick={() => deactivateMutation.mutate(user.id)}
                              >
                                Deactivate
                              </Button>
                            )}
                            {canDelete && (
                              <Button
                                size="small"
                                color="error"
                                variant="contained"
                                startIcon={<DeleteOutlineIcon />}
                                disabled={deleteMutation.isPending}
                                onClick={() => openDeleteDialog(user)}
                              >
                                Delete
                              </Button>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={data?.pagination?.total ?? 0}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 20, 50]}
            />
          </>
        )}
      </Paper>

      <DeleteAccountDialog
        open={Boolean(pendingDelete)}
        subjectKind="traveler"
        subjectName={pendingDelete ? travelerName(pendingDelete) : ''}
        isDeleting={deleteMutation.isPending}
        errorMessage={deleteErrorMessage}
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => pendingDelete && deleteMutation.mutate(pendingDelete.id)}
      />
    </Box>
  );
}
